'use client';

/* ──────────────────────────────────────────────
 *  CountdownTimer — Shows remaining time for
 *  pre-reservation, dispatches TIMER_EXPIRED
 * ────────────────────────────────────────────── */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

interface CountdownTimerProps {
  expiresAt: string;
  onExpired: () => void;
  reservedLabel: string;
  expiredLabel: string;
  rechooseLabel: string;
  onRechoose: () => void;
}

function formatTime(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export function CountdownTimer({
  expiresAt,
  onExpired,
  reservedLabel,
  expiredLabel,
  rechooseLabel,
  onRechoose,
}: CountdownTimerProps) {
  const expiresMs = useMemo((): number => new Date(expiresAt).getTime(), [expiresAt]);
  const onExpiredRef = useRef(onExpired);
  onExpiredRef.current = onExpired;

  const computeRemaining = useCallback(
    (): number => Math.max(0, Math.floor((expiresMs - Date.now()) / 1000)),
    [expiresMs],
  );

  const [remaining, setRemaining] = useState<number>(computeRemaining);

  useEffect((): (() => void) => {
    const tick = (): void => {
      const r = computeRemaining();
      setRemaining(r);
      if (r <= 0) {
        onExpiredRef.current();
      }
    };
    tick();
    const interval = setInterval(tick, 1000);
    return (): void => clearInterval(interval);
  }, [computeRemaining]);

  if (remaining <= 0) {
    return (
      <div className="bt-countdown bt-countdown--expired">
        <span className="bt-countdown__text">{expiredLabel}</span>
        <button type="button" className="bt-cta bt-cta--small bt-cta--outline" onClick={onRechoose}>
          {rechooseLabel}
        </button>
      </div>
    );
  }

  return (
    <div className="bt-countdown">
      <span className="bt-countdown__icon">⏱</span>
      <span className="bt-countdown__text">
        {reservedLabel} <strong>{formatTime(remaining)}</strong>
      </span>
    </div>
  );
}
